import { denormalize } from 'normalizr';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMeasure } from 'react-use';

import {
  Alert,
  AlertDescription,
  Box,
  ExpandableNode,
  Skeleton,
  isFooterNode,
  type AnyHierarchyNode,
  type ExpandableNodeHandle,
  type FooterNode,
  type NodeWithContext,
  type TableOrList,
} from '@frontend/legacy-components';

import { getVisualizedResultKey } from 'modules/common/forms/materialWidget/data/reducer';
import { getActiveSupportingFiles } from 'modules/viewerPage/data/reducer';

import { cadViewer, viewerInteractions } from '../data/actions';
import { viewerBodySchema } from '../data/partFileCadData/entities';
import {
  getCadData,
  getCadDataForPartFile,
} from '../data/partFileCadData/reducer';
import {
  getIsInteractionReady,
  getFocusedBodies,
  getSelectedBodies,
  getVisibleBodies,
} from '../data/reducer';
import {
  type CadNode,
  type CadPopoverNodeData,
  TreeNode,
  CadTreeContextMenuPopover,
  CadTreeThumbnailPopover,
} from './';

export const GEOMETRIC_SUPPORTING_FILES = 'geometric-supporting-files';
const DUMMY_ROOT_NODE = 'hidden-root';

type CadNodeWithContext = NodeWithContext<CadNode> & {
  toggleNodeExpanded(): boolean;
};

type SupportingFile = {
  uuid: string;
  hasGeometry: boolean;
  isDerivedFromInterrogation: boolean;
};

type PartFile = {
  containsVirus: boolean | null;
  fileType: string;
  filename: string;
  hasFailedChildren?: boolean;
  name: string;
  processingFailed?: boolean;
  uuid: string;
};
type ViewerPart = {
  id: number;
  type: string;
  partFile: string;
  viewerBodyViewerIds: string[];
};
type ViewerBody = {
  children: string[];
  descendentBodyViewerIds: string[];
  id: number;
  parentViewerId: string;
  viewerId: string;
  viewerName: string;
  viewerPart: number;
};

type CadDataEntities = {
  partFiles: Record<string, PartFile>;
  viewerParts: Record<string, ViewerPart>;
  viewerBodies: Record<string, ViewerBody>;
};
type CadData = {
  entities: CadDataEntities;
  isError: boolean;
  isLoading: boolean;
  rootViewerBodyViewerId: string;
};

type ViewerPartHydrated = Omit<ViewerPart, 'partFile'> & {
  partFile: PartFile;
};
type ViewerBodyHydrated = Omit<ViewerBody, 'viewerPart' | 'children'> & {
  children: ViewerBodyHydrated[];
  viewerPart: ViewerPartHydrated;
};

type SupportingFilesNode = {
  viewerId: string;
  children: ViewerBodyHydrated[];
};

type DummyRootNode = {
  viewerId: string;
  children: SupportingFilesNode[];
};

type mapViewerDataToTreeDataProps = {
  viewerBody: ViewerBodyHydrated | DummyRootNode;
  isParentFocused: boolean; // used to track if your parent is the "focused" node/body, so you can stay in the "active" state in the CAD tree
  isSupportingFile: boolean; // used to mark all nodes under "Geometric Supporting Files" so the depth spacing can be un-indented 1 column
  rootId: string | null; // keeps track of the root node of the tree, for resetting focus when nodes from other trees are interacted with
  focusedBodies: Set<string>;
  isDisabled: boolean;
  isAssemblyOrSubAssembly: boolean;
};

// Handles creating the tree data structure based on the Cad data of all files + Viewer state (focusedBodies)
const mapViewerDataToTreeData = ({
  viewerBody,
  isParentFocused,
  isSupportingFile,
  rootId,
  focusedBodies,
  isDisabled,
  isAssemblyOrSubAssembly = false,
}: mapViewerDataToTreeDataProps): CadNode => {
  const { children, viewerId } = viewerBody;

  if (viewerId === DUMMY_ROOT_NODE || viewerId === GEOMETRIC_SUPPORTING_FILES) {
    return {
      id: viewerId,
      viewerId,
      title:
        viewerId === DUMMY_ROOT_NODE
          ? DUMMY_ROOT_NODE
          : 'Geometric Supporting Files',
      children: (children || []).map((child) =>
        mapViewerDataToTreeData({
          viewerBody: child,
          isParentFocused: focusedBodies.has(viewerId),
          isSupportingFile:
            isSupportingFile || viewerId === GEOMETRIC_SUPPORTING_FILES,
          rootId: null, // set to null as this is not yet at the correct "root"
          focusedBodies,
          isDisabled,
          isAssemblyOrSubAssembly: true,
        }),
      ),
    } as CadNode;
  }

  const {
    viewerPart: {
      partFile: {
        containsVirus,
        filename,
        fileType,
        hasFailedChildren,
        name,
        processingFailed,
        uuid: partFileUuid,
      },
      viewerBodyViewerIds,
    },
  } = viewerBody as ViewerBodyHydrated;

  return {
    id: viewerId,
    viewerId,
    rootId: rootId ?? viewerId, // set to itself if no defined root, as this is used to get rootId of node
    containsVirus,
    filename,
    fileType,
    partFileUuid,
    viewerPartOccurances: viewerBodyViewerIds,
    isAssemblyOrSubAssembly: isAssemblyOrSubAssembly || !!children.length,
    title: name,
    isParentFocused,
    isSupportingFile,
    processingFailed,
    hasFailedChildren,
    isDisabled,
    children: (children || []).map((child) =>
      mapViewerDataToTreeData({
        viewerBody: child,
        isParentFocused: isParentFocused || focusedBodies.has(viewerId),
        isSupportingFile,
        rootId: rootId ?? viewerId,
        focusedBodies,
        isDisabled,
        isAssemblyOrSubAssembly: true,
      }),
    ),
  };
};

type CadTreeProps = {
  partFileUuid: string;
  partHasSupportingFiles: boolean;
  maxCadTreeHeight?: number;
};

export const CadTree = ({
  partFileUuid,
  partHasSupportingFiles = true,
  maxCadTreeHeight,
}: CadTreeProps) => {
  const dispatch = useDispatch();

  const [ref, { height = 1, width = 1 }] = useMeasure<HTMLDivElement>();

  const expandableNodeRef =
    useRef<ExpandableNodeHandle<AnyHierarchyNode, TableOrList>>(null);
  const { current: expandableNodeHandle } = expandableNodeRef ?? {};

  const isInteractionReady = useSelector(getIsInteractionReady);
  const isVisualizedResultKey = useSelector(getVisualizedResultKey);

  const _focusedBodies: string[] = useSelector(getFocusedBodies);
  const [focusedBodies, setFocusedBodies] = useState<Set<string>>(
    new Set(_focusedBodies),
  );

  const _selectedBodies: string[] = useSelector(getSelectedBodies);
  const selectedBodies: Set<string> = useMemo(
    () => new Set(_selectedBodies),
    [_selectedBodies],
  );
  const _visibleBodies: string[] = useSelector(getVisibleBodies);
  const visibleBodies: Set<string> = useMemo(
    () => new Set(_visibleBodies),
    [_visibleBodies],
  );

  // tracks the root id of the active body, for switching focus on new file interaction
  const [activeRootBodyId, setActiveRootBodyId] = useState<string | null>(null);

  // state for rendering popovers
  const [contextMenuPopoverNodeData, setContextMenuPopoverNodeData] =
    useState<CadPopoverNodeData | null>(null);
  const [thumbnailPopoverNodeData, setThumbnailPopoverNodeData] =
    useState<CadPopoverNodeData | null>(null);

  // Cad data of primary file
  // * Note: The primary file MAY not have CAD data, but the secondary supporting files may
  //   have geometry. (The secondary files with geometry are in allCADData)
  const cadData = useSelector((state) =>
    getCadDataForPartFile(state, partFileUuid),
  ) as unknown as CadData;

  // Cad data of primary file + supporting files
  const allCadData = useSelector(getCadData) as Record<string, CadData>;

  // Supporting file uuids for active file
  const _activeSupportingFiles: SupportingFile[] = useSelector(
    getActiveSupportingFiles,
  );

  const activeSupportingFiles = useMemo(
    () => (partHasSupportingFiles ? _activeSupportingFiles : []),
    [partHasSupportingFiles, _activeSupportingFiles],
  );

  const nonDerivedSupportingFileUuids = useMemo(
    () =>
      activeSupportingFiles.reduce<string[]>(
        (output, { uuid, hasGeometry, isDerivedFromInterrogation }) =>
          hasGeometry && !isDerivedFromInterrogation
            ? (output.push(uuid), output)
            : output,
        [],
      ),
    [activeSupportingFiles],
  );

  // map the cad data to the structure the tree component needs to display it
  const treeData = useMemo(() => {
    const { rootViewerBodyViewerId, entities } = cadData ?? {};

    const denormalizedCadTree: ViewerBodyHydrated | undefined =
      rootViewerBodyViewerId &&
      denormalize(rootViewerBodyViewerId, viewerBodySchema, entities);

    // get supporting file data
    let geometricSupportingFiles: SupportingFilesNode | undefined;
    if (nonDerivedSupportingFileUuids?.length) {
      const children: ViewerBodyHydrated[] = [];
      for (const fileUuid of nonDerivedSupportingFileUuids) {
        if (allCadData[fileUuid]?.rootViewerBodyViewerId) {
          children.push(
            denormalize(
              allCadData[fileUuid].rootViewerBodyViewerId,
              viewerBodySchema,
              allCadData[fileUuid].entities,
            ),
          );
        }
      }
      geometricSupportingFiles = {
        viewerId: GEOMETRIC_SUPPORTING_FILES,
        children: children,
      };
    }

    // Hidden root node to hold the primary tree + the supporting files trees
    const viewerData: DummyRootNode = {
      viewerId: DUMMY_ROOT_NODE,
      children: [
        //Note: it's possible the primary file does not have cadData (ie manual part), but its supporting files could have geometry
        //And it's possible to have NO children
        ...(denormalizedCadTree ? [denormalizedCadTree] : []),
        ...(geometricSupportingFiles ? [geometricSupportingFiles] : []),
      ],
    };

    return mapViewerDataToTreeData({
      viewerBody: viewerData,
      isParentFocused: false,
      isSupportingFile: false,
      rootId: null,
      focusedBodies,
      isDisabled: !isInteractionReady,
      isAssemblyOrSubAssembly: !!nonDerivedSupportingFileUuids?.length,
    });
  }, [
    cadData,
    allCadData,
    isInteractionReady,
    nonDerivedSupportingFileUuids,
    focusedBodies,
  ]);

  // handle update of focusedBodies Set
  // don't update local state of focusedBodies if in Online Metals app (indicated by isVisualizedResultKey), as this causes tree to collapse
  // local state of focusedBodies drive the 'bold' focus + expansion state in the CAD tree
  useEffect(() => {
    if (!isVisualizedResultKey) {
      setFocusedBodies(new Set(_focusedBodies));
    }
  }, [_focusedBodies, setFocusedBodies, isVisualizedResultKey]);

  // expand nodes based on focus
  // expand "geometric-supporting-files" by default
  useEffect(() => {
    if (expandableNodeHandle && treeData && focusedBodies.size) {
      // Collapse all to reset state
      expandableNodeHandle.collapseNodeRecursive(treeData);

      // Loop through focused Nodes and expand node + parents
      const stack = [treeData];
      let child: CadNode | undefined;
      while ((child = stack.pop())) {
        if (
          focusedBodies.has(child.viewerId) ||
          child.id === GEOMETRIC_SUPPORTING_FILES
        ) {
          expandableNodeHandle.expandParentsToRoot(child, treeData);
        }
        if (child.children?.length) {
          for (const nextChild of child.children ?? []) {
            stack.push(nextChild);
          }
        }
        // Set active root of the focused body
        if (focusedBodies.has(child.viewerId)) {
          setActiveRootBodyId(child.rootId);
        }
      }
    }
  }, [expandableNodeHandle, focusedBodies, treeData]);

  // Handles clicking on checkbox:
  //   Recalculates checkbox + visible states based on children states
  //   Determines whether checkbox is in current tree or in different tree
  //      If different tree, calls to change focus
  const handleCheck = useCallback(
    (node: CadNode, checked: boolean) => {
      // Use Set to track unique values
      const newVisibleBodies = new Set(visibleBodies);

      // If interacting with new root file:
      //   focus on the checked node
      //   handle check/uncheck nodes from other root files
      if (checked && node.rootId !== activeRootBodyId) {
        dispatch(
          viewerInteractions.focusOnBodies({ viewerBodyIds: [node.viewerId] }),
        );
        newVisibleBodies.clear();
      }

      // Handle propagating check / uncheck update to children
      if (node.children?.length) {
        const stack = [...node.children];
        let child: CadNode | undefined;

        // Only count last descendents as middle nodes may not be in the visibleBodies array
        while ((child = stack.pop())) {
          if (child.children?.length) {
            for (const nextChild of child.children ?? []) {
              stack.push(nextChild);
            }
          } else {
            if (checked) {
              newVisibleBodies.add(child.viewerId);
            } else {
              newVisibleBodies.delete(child.viewerId);
            }
          }
        }
      } else {
        // If no children, check / uncheck node directly
        if (checked) {
          newVisibleBodies.add(node.viewerId);
        } else {
          newVisibleBodies.delete(node.viewerId);
        }
      }

      // Update visible bodies with new cumulative selected nodes
      dispatch(
        cadViewer.receiveVisibleBodies({
          visibleBodies: [...newVisibleBodies],
        }),
      );
    },
    [dispatch, visibleBodies, activeRootBodyId],
  );

  // Handles select / highlight state for the tree + Viewer
  const handleSelect = useCallback(
    (viewerId: string, remove: boolean) => {
      const newSelectedBodies = !remove
        ? [...selectedBodies, viewerId]
        : [...selectedBodies].filter(
            (selectedBodyId: string) => selectedBodyId !== viewerId,
          );

      dispatch(
        cadViewer.receiveSelectedBodies({
          selectedBodies: newSelectedBodies,
        }),
      );
    },
    [dispatch, selectedBodies],
  );

  // Handles context menu for additional options on each node (zoom, focus, isolate, etc.)
  const handleRightClick = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement> | null,
      popoverNodeData: CadPopoverNodeData,
    ) => {
      e?.preventDefault();
      if (!isInteractionReady) return;
      setContextMenuPopoverNodeData(popoverNodeData);
    },
    [isInteractionReady],
  );

  // Handles Part Thumbnail popover on FileTypeIcon hover
  const handleThumbnailHover = useCallback(
    (popoverNodeData: CadPopoverNodeData | null) => {
      setThumbnailPopoverNodeData(popoverNodeData);
    },
    [],
  );

  if (cadData?.isError) {
    return (
      <Box mx={1}>
        <Alert h="24px" status="error">
          <AlertDescription>Unable to display CAD data.</AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (treeData && treeData.children.length === 0) {
    return null;
  }

  if (cadData?.isLoading && !treeData) {
    return <Skeleton mx={1} height={'24px'} />;
  }

  return (
    <Box
      ref={ref}
      h={maxCadTreeHeight ? '100%' : 'calc(100% - 42px)'} // subtract height of the searchbar
      w="100%"
      position="relative"
    >
      <CadTreeContextMenuPopover
        popoverNodeData={contextMenuPopoverNodeData}
        activeRootBodyId={activeRootBodyId}
        handleCloseMenu={() => setContextMenuPopoverNodeData(null)}
      />
      <CadTreeThumbnailPopover popoverNodeData={thumbnailPopoverNodeData} />
      <ExpandableNode
        ref={expandableNodeRef}
        components={{}}
        root={treeData ?? {}}
        height={height}
        width={width}
        {...(maxCadTreeHeight
          ? {
              dynamicHeightProps: {
                minHeight: 0,
                maxHeight: maxCadTreeHeight,
                itemHeightEstimate: 28, //line height in pixels (assumes text doesn't wrap)
                additionalHeight: 42,
                padding: 8,
                excludeFooter: true,
              },
            }
          : {})}
        hideRoot
        yieldFooterNode
        footerNodePadding
        // This increases the top and bottom of the viewport by a value equal to the height (arbitrary multiple).
        // It pre-mounts the DOM with nodes so they load "quicker" on scroll, without too much impact on list size.
        increaseViewportBy={maxCadTreeHeight ? maxCadTreeHeight : height}
        wrapperType="list"
        nodeContent={(
          props: NodeWithContext<AnyHierarchyNode | FooterNode>,
        ) => {
          if (isFooterNode(props.node)) {
            return <Box h={1} />;
          }
          return (
            <TreeNode
              key={(props.node as CadNode).viewerId}
              {...(props as CadNodeWithContext)}
              selectedKeys={selectedBodies}
              checkedKeys={visibleBodies}
              focusedKeys={focusedBodies}
              handleCheck={handleCheck}
              handleSelect={handleSelect}
              handleRightClick={handleRightClick}
              handleThumbnailHover={handleThumbnailHover}
            />
          );
        }}
      />
    </Box>
  );
};
